/**
 * Microsoft 365 Copilot Chat can embed PDF figures in answers.
 * LabWizard uses Copilot Studio, which does not return those images — so we
 * rasterize selected pages from the attached file and inject them as markdown.
 */

import { pdfjs } from "react-pdf"

export type PdfFigure = {
  page: number
  dataUrl: string
}

const PAGE_BLOCK = /(?:^|\n)Page (\d+):\s*([\s\S]*?)(?=\nPage \d+:|$)/gi

function wantsInstallFigures(question: string) {
  return /\b(?:install|steps?|procedure|unpack|gas\s*injection|before\s+installation|how\s+to)\b/i.test(
    String(question || "")
  )
}

/** Pages that look like they hold installation / figure content worth showing. */
export function selectPdfFigurePages(
  extraction: string,
  question: string,
  maxPages = 3
): number[] {
  const scores = new Map<number, number>()
  const bump = (page: number, score: number) => {
    if (page < 1 || page > 500) return
    scores.set(page, (scores.get(page) || 0) + score)
  }

  const asked = String(question || "")
  const install = wantsInstallFigures(asked)

  for (const match of String(extraction || "").matchAll(PAGE_BLOCK)) {
    const page = Number(match[1])
    const text = match[2] || ""
    let score = 0
    if (/fig\.?\s*\d+|figure\s*\d+|diagram|illustration|labels?\s*:/i.test(text)) {
      score += 3
    }
    if (
      install &&
      /install|unpack|gas\s*injection|before\s+installation|levell?ing|connecting a gas/i.test(
        text
      )
    ) {
      score += 5
    }
    if (score) bump(page, score)
  }

  for (const hit of asked.matchAll(/\b(?:pdf\s*)?pages?\s+(\d{1,3})\b/gi)) {
    bump(Number(hit[1]), 4)
  }

  const ranked = [...scores.entries()]
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .slice(0, maxPages)
    .map(([page]) => page)
    .sort((a, b) => a - b)

  if (ranked.length) return ranked

  if (install) {
    const mentioned = [
      ...String(extraction || "").matchAll(/(?:^|\n)Page (\d+):/gi),
    ].map((hit) => Number(hit[1]))
    const late = [...new Set(mentioned)].filter((page) => page >= 7).slice(0, maxPages)
    if (late.length) return late
  }
  return []
}

/**
 * When extraction is empty or unhelpful, still pick figure pages from the PDF
 * itself (install manuals usually put Fig.1 near the end).
 */
export async function resolvePdfFigurePages(
  file: File,
  extraction: string,
  question: string,
  maxPages = 3
): Promise<number[]> {
  const fromText = selectPdfFigurePages(extraction, question, maxPages)
  if (fromText.length) return fromText
  if (!file) return []
  try {
    const data = await file.arrayBuffer()
    const pdf = await pdfjs.getDocument({ data }).promise
    const total = pdf.numPages || 0
    if (!total) return []
    if (wantsInstallFigures(question)) {
      const candidates = [9, 10, 8, 7, 11, 6]
        .filter((page) => page >= 1 && page <= total)
        .slice(0, maxPages)
      if (candidates.length) return candidates
    }
    return Array.from({ length: Math.min(maxPages, total) }, (_, index) => index + 1)
  } catch {
    return []
  }
}

/** Rasterize PDF pages to JPEG data URLs for inline chat display. */
export async function renderPdfFigures(
  file: File,
  pages: number[],
  options?: { scale?: number; quality?: number }
): Promise<PdfFigure[]> {
  if (!file || !pages.length) return []
  const scale = options?.scale ?? 1.5
  const quality = options?.quality ?? 0.78
  const data = await file.arrayBuffer()
  const pdf = await pdfjs.getDocument({ data }).promise
  const figures: PdfFigure[] = []
  for (const pageNumber of pages) {
    if (pageNumber < 1 || pageNumber > pdf.numPages) continue
    // Pages are rendered one at a time to keep memory predictable for large manuals.
    // eslint-disable-next-line no-await-in-loop
    const page = await pdf.getPage(pageNumber)
    const viewport = page.getViewport({ scale })
    const canvas = document.createElement("canvas")
    canvas.width = Math.ceil(viewport.width)
    canvas.height = Math.ceil(viewport.height)
    const context = canvas.getContext("2d")
    if (!context) continue
    // eslint-disable-next-line no-await-in-loop
    await page.render({ canvasContext: context, viewport }).promise
    figures.push({
      page: pageNumber,
      dataUrl: canvas.toDataURL("image/jpeg", quality),
    })
  }
  return figures
}

/** Insert PDF figures under Step 1 (or the first section), like M365 Copilot Chat. */
export function withPdfFigures(answer: string, figures: PdfFigure[]) {
  const text = String(answer || "").trim()
  if (!text || !figures.length) return text
  const block = figures
    .map(
      (figure) =>
        `![Figure from page ${figure.page} of the attached PDF](${figure.dataUrl})\nSource: attached PDF, page ${figure.page}`
    )
    .join("\n\n")

  // Studio often uses "## Step 1", "**Step 1 — …**", or "Step 1:" without a heading mark.
  const step1 =
    /^(#{1,3}\s*)?(?:\*\*)?Step\s*1\b[^\n]*(?:\*\*)?\s*\n+/im
  if (step1.test(text)) {
    return text.replace(step1, (match) => `${match.trimEnd()}\n\n${block}\n\n`)
  }
  const firstHeading = /^(#{1,3}\s+[^\n]+|\*\*[^*\n]+\*\*)\s*\n+/m
  if (firstHeading.test(text)) {
    return text.replace(firstHeading, (match) => `${match.trimEnd()}\n\n${block}\n\n`)
  }
  return `${text}\n\n${block}`
}
